import React, { useState, useEffect, useRef } from 'react';
import { Layout, Button } from 'antd';
import { DownloadOutlined, MenuOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';

const { Header: AntHeader } = Layout;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Handle scroll to show/hide header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 10;

      if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
        return;
      }

      if (currentScrollY < 100) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
        setMobileMenuOpen(false);
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [lastScrollY]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 767) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Logo点击处理函数
  const handleLogoClick = () => {
    if (location.pathname === '/') {
      // 如果在首页，滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // 如果不在首页，导航到首页
      navigate('/');
    }
  };

  // About Me点击处理函数
  const handleAboutMeClick = () => {
    if (location.pathname === '/') {
      // 如果在首页，滚动到About Me部分
      const aboutSection = document.getElementById('about');
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // 如果不在首页，先导航到首页，然后滚动
      navigate('/');
      setTimeout(() => {
        const aboutSection = document.getElementById('about');
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    closeMobileMenu();
  };

  // Case Studies点击处理函数 - 直接跳转到My Vision部分
  const handleCaseStudiesClick = () => {
    if (location.pathname === '/') {
      // 如果在首页，滚动到My Vision部分
      const mycaseSection = document.getElementById('mycase');
      if (mycaseSection) {
        mycaseSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // 如果不在首页，先导航到首页，然后滚动
      navigate('/');
      setTimeout(() => {
        const mycaseSection = document.getElementById('mycase');
        if (mycaseSection) {
          mycaseSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    closeMobileMenu();
  };

  // Download Resume点击处理函数
  const handleDownloadResume = () => {
    window.open('https://drive.google.com/file/d/1RRudLU1VjgWln0ZgXqp5SmSzNtJwOXVc/view?usp=drive_link', '_blank');
    closeMobileMenu();
  };

  return (
    <div ref={headerRef} className={`header-wrapper ${isHeaderVisible ? 'visible' : 'hidden'}`}>
      <AntHeader className="app-header">
        <nav className="navbar">
          {/* Logo Section - 点击回到主页 */}
          <div className="logo-section" onClick={handleLogoClick}>
            <img
              src="/images/logo/logo.png"
              alt="Personal Portfolio Logo"
              className="header-logo"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="nav-items desktop-nav">
            <span className="nav-item" onClick={handleAboutMeClick}>About Me</span>
            <span className="nav-item" onClick={handleCaseStudiesClick}>Case Studies</span>
            <Button type="primary" icon={<DownloadOutlined />} className="download-resume-btn" onClick={handleDownloadResume}>
              Download Resume
            </Button>
          </div>

          {/* Mobile Navigation */}
          <div className="nav-items mobile-nav">
            <Button
              type="text"
              icon={<MenuOutlined />}
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
            />
            <Button type="primary" icon={<DownloadOutlined />} className="download-resume-btn mobile-download-btn" onClick={handleDownloadResume}>
              Resume
            </Button>
          </div>
        </nav>
      </AntHeader>

      {/* Mobile Dropdown Menu */}
      <div className={`mobile-dropdown ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-dropdown-content">
          <div className="mobile-menu-section">
            <div className="mobile-menu-title">Navigation</div>
            <div className="mobile-menu-items">
              <span className="mobile-nav-item" onClick={handleAboutMeClick}>About Me</span>
              <span className="mobile-nav-item" onClick={handleCaseStudiesClick}>Case Studies</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
